import * as React from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessagePlugin, Upload } from 'tdesign-react';
import { UploadRef } from 'tdesign-react/es/upload/interface';

import { EAsyncStatus, RichMediaValue } from '@/interface/media';

import { uploadCos } from '../../service';

export interface RefUploadFileProps {
  value?: File[];
  richValue?: RichMediaValue | undefined;
  onChange: (params: { url: string; name: string }) => any;
  asyncUploadOnChange?: (file?: RichMediaValue) => any;// 剧情中卸载后处理图片loading恢复
  disabled?: boolean;
  limit?: {
    fileType?: string[];
    size?: number;
    count?: number;
    uploadType?: string[];
  };
  multiple?: boolean;
  width?: number | string;
  height?: number | string;
  wrapStyle?: any;
  uploadRef: React.ForwardedRef<{
    upload: () => void;
    status: () => UploadStatus;
  }> | null;
}

export enum UploadStatus {
  INIT = 'init',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

export const RefUpload: React.FC<RefUploadFileProps> = (props) => {
  const {
    onChange,
    asyncUploadOnChange,
    width: wrapWidth = 300,
    disabled = false,
    limit,
    multiple,
    value = [],
    uploadRef,
  } = props;
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('id') || '';
  const [status, setStatus] = useState(UploadStatus.INIT);

  useImperativeHandle(uploadRef, () => ({
    upload: () => handleClickUploadClick(),
    status: () => status,
  }));

  const tdUploadElementRef = useRef<UploadRef>(null);

  const handleAsyncLoading = useCallback(() => {
    // 设置异步上传loading状态  组件卸载后，loading state 重置
    // 所以需要上层组件传值恢复组件loading状态
    asyncUploadOnChange?.({
      asyncStatus: EAsyncStatus.UPLOAD_LOADING,
    });
  }, [asyncUploadOnChange]);

  const handleAsyncInit = useCallback(() => {
    // 设置异步上传loading状态 有onChange的地方就不需要调用者，如果没有则需要调用
    asyncUploadOnChange?.({
      asyncStatus: EAsyncStatus.UPLOAD_INIT,
    });
  }, [asyncUploadOnChange]);

  const handleChange = (value: unknown[]) => {
    const result = value?.[0] as {
      url: string;
      name: string;
    };
    onChange(result);
    setStatus(UploadStatus.SUCCESS);
  };

  const handleClickUploadClick = () => {
    tdUploadElementRef?.current?.triggerUpload?.();
  };

  const uploadFile = async (file: any) => {
    setStatus(UploadStatus.LOADING);
    handleAsyncLoading();
    const res = await uploadCos(file.raw, { storyId });
    if (!res?.response?.url) {
      setStatus(UploadStatus.ERROR);
      handleAsyncInit();
      MessagePlugin.error('上传失败,请重试');
    }
    return res;
  };

  const msgInfo = {
    type: `格式：${limit?.fileType?.join('、')}`,
    size: `大小：不超过 ${limit?.size}MB`,
  };

  const isType = (val: string) => {
    const isPass = limit?.uploadType?.includes(val);
    if (!isPass) {
      MessagePlugin.error(msgInfo.type);
    }
    return isPass;
  };

  const isSize = (val: number) => {
    const isPass = val <= (limit?.size || 10) * 1024 * 1024;
    if (!isPass) {
      MessagePlugin.error(msgInfo.size);
    }
    return isPass;
  };

  const beforeUpload = (file: any) => Boolean(isType(file.type) && isSize(file.size));

  return (
    <Upload
      ref={tdUploadElementRef}
      disabled={disabled}
      onChange={handleChange}
      style={{ width: wrapWidth, display: 'none' }}
      max={limit?.count || undefined}
      multiple={multiple}
      requestMethod={uploadFile}
      showUploadProgress={true}
      showImageFileName={false}
      allowUploadDuplicateFile={true}
      files={value}
      beforeUpload={beforeUpload}
    />
  );
};

export default RefUpload;
